import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { JourneyShell } from "@/components/journey/JourneyShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/auth/AuthContext";
import { useJourneyContent } from "@/hooks/useJourneyContent";
import { saveProfile } from "@/lib/journeyApi";
import "@/pages/Journey.css";

const safeReturnTo = (value) => (value?.startsWith("/") ? value : "/check");

export default function Profile() {
  const { user, setUser } = useAuth();
  const { ui, loading } = useJourneyContent();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({ age: "", country: "", exercise: "", alcohol: "", diet: "", bg: "" });
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user) return;
    setForm({
      age: user.age ? String(user.age) : "",
      country: user.country || "",
      exercise: user.exercise || "",
      alcohol: user.alcohol || "",
      diet: user.diet || "",
      bg: user.bg || "",
    });
  }, [user]);

  if (loading || !ui || !user) return <div className="journey-loading" data-testid="profile-loading-state" />;
  const profile = ui.profile;
  const setField = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const submit = async (event) => {
    event.preventDefault();
    const age = Number(form.age);
    if (!Number.isInteger(age) || age < 18 || age > 70 || !form.country) {
      setError(age < 18 ? profile.under_18 : profile.age_error);
      return;
    }
    try {
      const updated = await saveProfile({
        age,
        country: form.country,
        exercise: form.exercise || null,
        alcohol: form.alcohol || null,
        diet: form.diet || null,
        bg: form.bg || null,
      });
      setUser(updated);
      navigate(safeReturnTo(searchParams.get("returnTo")), { replace: true });
    } catch (requestError) {
      setError(requestError.response?.data?.detail || profile.age_error);
    }
  };

  const selectField = (id, label, options, required = false) => (
    <label className="journey-field" htmlFor={id}>
      <span>{label}</span>
      <select
        id={id}
        value={form[id]}
        onChange={(event) => setField(id, event.target.value)}
        required={required}
        data-testid={`profile-${id}-select`}
      >
        <option value="" />
        {options.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
      </select>
    </label>
  );

  return (
    <JourneyShell>
      <form className="journey-panel" onSubmit={submit} data-testid="profile-form">
        <h1 data-testid="profile-title">{profile.title}</h1>
        <p data-testid="profile-body">{profile.body}</p>
        <label className="journey-field" htmlFor="age">
          <span>{profile.age_label}</span>
          <Input id="age" type="number" min="18" max="70" value={form.age} onChange={(event) => setField("age", event.target.value)} data-testid="profile-age-input" />
        </label>
        {selectField("country", ui.login.country_label, ui.login.countries, true)}
        <div className="journey-divider" />
        <h2>{profile.optional_title}</h2>
        <p>{profile.skip_note}</p>
        <div className="journey-grid">
          {selectField("exercise", profile.exercise_label, profile.exercise_options)}
          {selectField("alcohol", profile.alcohol_label, profile.alcohol_options)}
          {selectField("diet", profile.diet_label, profile.diet_options)}
          {selectField("bg", profile.bg_label, profile.bg_options)}
        </div>
        {error && <p className="journey-error" role="alert" data-testid="profile-error">{error}</p>}
        <Button type="submit" className="journey-primary" data-testid="profile-submit-button">{profile.button}</Button>
      </form>
    </JourneyShell>
  );
}